import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ChevronDown } from "lucide-react";
import { motion, AnimatePresence, ease as mmEase } from "@/lib/motion";
import DarkCalcDropdown from "../components/DarkCalcDropdown";
import FloatingCurrencyButton from "../components/FloatingCurrencyButton";
import { RATES, FLAGS, CAST } from "../data";

const ez = mmEase.expo;

// Cinematic phase sequence (ms)
// Phase 1: right-column cards rise
// Phase 2: second card
// Phase 3: Thread visible (drawn by HeroCurve itself via The Awakening)
// Phase 4: transfer confirmed state
// Phase 5: headline fades in
// Phase 6: CTAs appear
const PHASES = [200, 700, 1100, 2200, 2700, 3200];

// Michael photo (design_guidelines.json: user_sending_money)
const MICHAEL_PHOTO = "https://images.unsplash.com/photo-1726690323438-addd828bbccb?w=900&q=80&auto=format&fit=crop";

export default function CinematicHero({ goToApp, user }) {
  const navigate = useNavigate();
  const [phase,     setPhase    ] = useState(0);
  const [amount,    setAmount   ] = useState(500);
  const [from,      setFrom     ] = useState("GBP");
  const [to,        setTo       ] = useState("NGN");
  const [fromOpen,  setFromOpen ] = useState(false);
  const [toOpen,    setToOpen   ] = useState(false);
  const [arriving,  setArriving ] = useState(false); // The Arrival ring animation

  const fromRef    = useRef(null);
  const toRef      = useRef(null);
  const sectionRef = useRef(null);

  const feeAmt     = +(amount * 0.005).toFixed(2);
  const rate       = RATES[to] / RATES[from];
  const receiveAmt = +((amount - feeAmt) * rate).toFixed(0);

  // Cinematic phase timers
  useEffect(() => {
    const timers = PHASES.map((ms, i) => setTimeout(() => setPhase(i + 1), ms));
    return () => timers.forEach(clearTimeout);
  }, []);

  // Close dropdowns on outside click — skip clicks inside the portaled dropdown
  useEffect(() => {
    const fn = (e) => {
      if (e.target.closest("[data-mm-dropdown]")) return;
      if (fromRef.current && !fromRef.current.contains(e.target)) setFromOpen(false);
      if (toRef.current   && !toRef.current.contains(e.target))   setToOpen(false);
    };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);


  // The Arrival: ring animation on CTA activation
  //
  // Production bug fix — this used window.location.href = "/register",
  // a hard browser navigation. That forces a real HTTP request straight
  // at the server for a path that only React Router (BrowserRouter, see
  // App.js) knows how to render — bypassing the already-loaded SPA
  // entirely. On a server with no SPA-fallback rewrite configured (the
  // production deployment did not have one), that request 404s instead
  // of ever reaching index.html. Every other "go to register" call site
  // in this codebase already used react-router's navigate()/<Link>,
  // which stays client-side and never hits the server at all — this was
  // the one place that didn't.
  const handleCTA = useCallback(() => {
    setArriving(true);
    setTimeout(() => setArriving(false), 700);
    if (user) goToApp();
    else navigate("/register");
  }, [user, goToApp, navigate]);

  const sendCurrencies    = Object.keys(RATES);
  const receiveCurrencies = Object.keys(RATES).filter((c) => c !== from);

  const cardBase = {
    background: "rgba(255,255,255,0.038)",
    border: "1px solid rgba(255,255,255,0.09)",
    borderRadius: "20px",
    padding: "20px",
  };
  const labelSm = {
    color: "rgba(255,255,255,0.26)", fontSize: "9px", fontWeight: 700,
    textTransform: "uppercase", letterSpacing: "0.22em", marginBottom: "6px",
  };
  const inputRow = (highlight) => ({
    display: "flex", alignItems: "center", gap: "8px",
    background: highlight ? "rgba(16,185,129,0.08)" : "rgba(255,255,255,0.045)",
    border: `1px solid ${highlight ? "rgba(16,185,129,0.22)" : "rgba(255,255,255,0.07)"}`,
    borderRadius: "12px", padding: "10px 14px",
    transition: "all 0.5s ease",
  });
  const cxBtn = {
    display: "flex", alignItems: "center", gap: "5px",
    padding: "6px 10px", borderRadius: "8px",
    background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.11)",
    color: "rgba(255,255,255,0.78)", fontSize: "12px", fontWeight: 700,
    cursor: "pointer", whiteSpace: "nowrap",
  };

  return (
    <section
      ref={sectionRef}
      style={{
        minHeight: "100vh",
        background: "hsl(var(--mm-void))",
        marginTop: "68px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <style>{`@keyframes dashMove { to { stroke-dashoffset: -11; } }`}</style>
      {/* ── Michael photo — left editorial backdrop (The Thread composition) ── */}
      <div
        className="absolute inset-0 pointer-events-none"
        aria-hidden="true"
      >
        <div className="photo-duotone" style={{
          position: "absolute",
          left: 0, top: 0,
          width: "58%", height: "100%",
        }}>
          <img
            src={MICHAEL_PHOTO}
            alt=""
            aria-hidden="true"
            style={{ position: "absolute", inset: 0 }}
          />
          <div className="photo-duotone-overlay" />
        </div>
        {/* Gradient fade from photo into void */}
        <div style={{
          position: "absolute",
          left: 0, top: 0,
          width: "68%", height: "100%",
          background: "linear-gradient(90deg, transparent 30%, hsl(var(--mm-void)) 90%)",
        }} />
        {/* Bottom fade */}
        <div style={{
          position: "absolute",
          inset: 0,
          background: "linear-gradient(to top, hsl(var(--mm-void)) 0%, transparent 30%)",
        }} />
      </div>

      {/* ── Atmospheric glows ── */}
      <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
        <div style={{
          position: "absolute", top: "18%", left: "8%",
          width: "48vw", height: "48vw", borderRadius: "50%",
          background: "radial-gradient(circle, rgba(16,185,129,0.055) 0%, transparent 65%)",
          filter: "blur(72px)",
        }} />
      </div>

      {/* ── 70 / 30 layout ────────────────────────────────────────── */}
      <div style={{
        position: "relative", zIndex: 10, width: "100%",
        maxWidth: "1200px", padding: "64px 24px 64px",
      }}>
        <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-14">

          {/* LEFT — Narrative: Thread + Headline + CTA (≈65%) */}
          <div className="flex-1 min-w-0 flex flex-col">


            {/* Headline — appears after Thread draw completes */}
            <motion.div
              initial={{ opacity: 0, y: 32, filter: "blur(8px)" }}
              animate={phase >= 5 ? { opacity: 1, y: 0, filter: "blur(0px)" } : {}}
              transition={{ duration: 0.92, ease: ez }}
              style={{ marginBottom: "24px" }}
            >
              <h1
                style={{
                  fontFamily: "'Fraunces', Georgia, serif",
                  fontWeight: 900, color: "white", lineHeight: 1.0,
                  letterSpacing: "-0.04em",
                  fontSize: "clamp(2.6rem, 5.5vw, 5.2rem)",
                  marginBottom: "16px",
                  textWrap: "balance",
                }}
              >
                Finally, someone<br />
                built this{" "}
                <span style={{ color: "var(--color-success)", fontStyle: "italic" }}>for&nbsp;us.</span>
              </h1>
              <p style={{
                color: "rgba(255,255,255,0.52)", fontSize: "1.05rem",
                maxWidth: "420px", lineHeight: 1.72,
                fontFamily: "'Inter', system-ui, sans-serif",
              }}>
                Send money to Africa, tracked every step of the way. The real mid-market rate. A flat 0.5% fee.
              </p>
            </motion.div>

            {/* CTAs — The Arrival ring wraps primary CTA */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={phase >= 6 ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, ease: ez }}
              className="flex flex-wrap items-center gap-3 mb-6"
            >
              {/* The Arrival: ring SVG around primary CTA */}
              <div style={{ position: "relative", display: "inline-flex" }}>
                <FloatingCurrencyButton
                  data-testid="hero-cta-start"
                  onClick={handleCTA}
                  label={user ? "Open app" : "Open free account"}
                />
                <AnimatePresence>
                  {arriving && (
                    <motion.svg
                      key="arrival-ring"
                      viewBox="-4 -4 8 8"
                      style={{
                        position: "absolute",
                        top: "50%", left: "50%",
                        pointerEvents: "none",
                        overflow: "visible",
                        transform: "translate(-50%, -50%)",
                      }}
                      width="0"
                      height="0"
                    >
                      {[0, 100, 200].map((delay, i) => (
                        <motion.circle
                          key={i}
                          cx="0" cy="0"
                          initial={{ r: 4, opacity: 0.55, strokeWidth: 2 }}
                          animate={{ r: 32, opacity: 0, strokeWidth: 0.5 }}
                          transition={{ duration: 0.65, ease: "easeOut", delay: delay / 1000 }}
                          fill="none"
                          stroke="var(--color-success)"
                        />
                      ))}
                    </motion.svg>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>

          </div>

          {/* RIGHT — Calculator cards (≈35%) */}
          <div className="w-full lg:w-[330px] shrink-0 flex flex-col gap-3">


            {/* Michael — sender card */}
            <motion.div
              initial={{ opacity: 0, x: 28, scale: 0.94 }}
              animate={phase >= 1 ? { opacity: 1, x: 0, scale: 1 } : {}}
              transition={{ duration: 0.72, ease: ez }}
              style={{ position: "relative", zIndex: fromOpen ? 20 : 1 }}
            >
              <div style={cardBase}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "16px" }}>
                  <div style={{
                    width: "40px", height: "40px", borderRadius: "12px", flexShrink: 0,
                    background: "linear-gradient(135deg,#1a4a6e,#0d2d45)",
                    display: "flex", alignItems: "center", justifyContent: "center", fontSize: "19px",
                  }}>
                    {CAST.michael.emoji}
                  </div>
                  <div>
                    <p style={{ color: "rgba(255,255,255,0.88)", fontWeight: 700, fontSize: "13px" }}>
                      {CAST.michael.name}
                    </p>
                    <p style={{ color: "rgba(255,255,255,0.32)", fontSize: "10px" }}>
                      {CAST.michael.country} {CAST.michael.city}
                    </p>
                  </div>
                </div>

                <p style={labelSm}>You send</p>
                <div style={{ ...inputRow(false), marginBottom: "8px" }}>
                  <input
                    data-testid="hero-quote-send"
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(Math.max(0, Number(e.target.value)))}
                    style={{
                      flex: 1, minWidth: 0, fontSize: "1.65rem", fontWeight: 900,
                      color: "white", background: "transparent", outline: "none",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  />
                  <div ref={fromRef} style={{ position: "relative", flexShrink: 0 }}>
                    <button
                      data-testid="hero-quote-from"
                      onClick={() => { setFromOpen((p) => !p); setToOpen(false); }}
                      style={cxBtn}
                    >
                      <span style={{ fontSize: "15px" }}>{FLAGS[from]}</span>
                      <span>{from}</span>
                      <ChevronDown
                        size={10} strokeWidth={2}
                        style={{
                          color: "rgba(255,255,255,0.30)",
                          transform: fromOpen ? "rotate(180deg)" : undefined,
                          transition: "transform 0.2s",
                        }}
                      />
                    </button>
                    <AnimatePresence>
                      {fromOpen && (
                        <DarkCalcDropdown
                          currencies={sendCurrencies}
                          selected={from}
                          onSelect={(c) => { setFrom(c); setFromOpen(false); }}
                          triggerRef={fromRef}
                        />
                      )}
                    </AnimatePresence>
                  </div>
                </div>

                <p style={{ fontSize: "10px", color: "rgba(255,255,255,0.20)", textAlign: "center" }}>
                  Fee: {feeAmt} {from} · Rate: mid-market
                </p>
              </div>
            </motion.div>

            {/* TransferArc — THE signature interaction. The Thread connecting sender to receiver. */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={phase >= 3 ? { opacity: 1 } : {}}
              transition={{ duration: 0.3, ease: ez }}
              className="hidden lg:block relative"
              style={{ height: "52px" }}
            >
              <svg
                viewBox="0 0 330 52"
                fill="none"
                style={{ width: "100%", height: "52px", overflow: "visible" }}
                aria-hidden="true"
              >
                <defs>
                  <linearGradient id="ta-grad" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="var(--color-success)" />
                    <stop offset="100%" stopColor="#06B6D4" />
                  </linearGradient>
                </defs>
                <path
                  id="ta-path"
                  d="M 165 2 C 235 16, 95 36, 165 50"
                  stroke="url(#ta-grad)"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  fill="none"
                  style={{
                    strokeDasharray: phase >= 4 ? "6 5" : "120",
                    strokeDashoffset: phase >= 3 ? 0 : 120,
                    transition: phase === 3 ? "stroke-dashoffset 0.72s cubic-bezier(0.16,1,0.3,1)" : "none",
                    animation: phase >= 4 ? "dashMove 1.8s linear infinite" : "none",
                  }}
                />
                {phase >= 4 && (
                  <>
                    <circle r="9" fill="var(--color-success)" opacity="0.12">
                      <animateMotion dur="1.1s" repeatCount="indefinite"><mpath href="#ta-path" /></animateMotion>
                    </circle>
                    <circle r="4.5" fill="var(--color-success)" opacity="0.8">
                      <animateMotion dur="1.1s" repeatCount="indefinite"><mpath href="#ta-path" /></animateMotion>
                    </circle>
                    <circle r="2" fill="white">
                      <animateMotion dur="1.1s" repeatCount="indefinite"><mpath href="#ta-path" /></animateMotion>
                    </circle>
                  </>
                )}
              </svg>
              {phase >= 4 && (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                  style={{
                    position: "absolute", bottom: 2, right: 0,
                    fontSize: "9px", fontWeight: 800, letterSpacing: "0.05em",
                    color: "var(--color-success)",
                  }}
                >
                  ✓ 1m 21s
                </motion.p>
              )}
            </motion.div>

            {/* Mama — receiver card */}
            <motion.div
              initial={{ opacity: 0, x: 28, scale: 0.94 }}
              animate={phase >= 2 ? { opacity: 1, x: 0, scale: 1 } : {}}
              transition={{ duration: 0.72, ease: ez }}
              style={{ position: "relative", zIndex: toOpen ? 20 : 1 }}
            >
              <div style={{
                ...cardBase,
                border: `1px solid ${phase >= 4 ? "rgba(16,185,129,0.30)" : "rgba(255,255,255,0.09)"}`,
                transition: "border-color 0.6s ease",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "16px" }}>
                  <div style={{
                    width: "40px", height: "40px", borderRadius: "12px", flexShrink: 0,
                    background: "linear-gradient(135deg,#1a4a2e,#0d2d1a)",
                    display: "flex", alignItems: "center", justifyContent: "center", fontSize: "19px",
                  }}>
                    {CAST.mama.emoji}
                  </div>
                  <div>
                    <p style={{ color: "rgba(255,255,255,0.88)", fontWeight: 700, fontSize: "13px" }}>
                      {CAST.mama.name}
                    </p>
                    <p style={{ color: "rgba(255,255,255,0.32)", fontSize: "10px" }}>
                      {CAST.mama.country} {CAST.mama.city}
                    </p>
                  </div>
                  <AnimatePresence>
                    {phase >= 4 && (
                      <motion.span
                        initial={{ opacity: 0, scale: 0.72 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.38, ease: ez }}
                        style={{
                          marginLeft: "auto", fontSize: "10px", fontWeight: 700,
                          background: "rgba(16,185,129,0.14)", color: "var(--color-success)",
                          padding: "3px 9px", borderRadius: "20px",
                          border: "1px solid rgba(16,185,129,0.24)",
                        }}
                      >
                        ✓ Received
                      </motion.span>
                    )}
                  </AnimatePresence>
                </div>

                <p style={labelSm}>She receives</p>
                <div style={{ ...inputRow(phase >= 4), marginBottom: "10px" }}>
                  <p style={{
                    flex: 1, fontSize: "1.65rem", fontWeight: 900,
                    color: phase >= 4 ? "var(--color-success)" : "white",
                    fontVariantNumeric: "tabular-nums",
                    transition: "color 0.5s ease",
                  }}>
                    {receiveAmt.toLocaleString("en-US")}
                  </p>
                  <div ref={toRef} style={{ position: "relative", flexShrink: 0 }}>
                    <button
                      data-testid="hero-quote-to"
                      onClick={() => { setToOpen((p) => !p); setFromOpen(false); }}
                      style={cxBtn}
                    >
                      <span style={{ fontSize: "15px" }}>{FLAGS[to]}</span>
                      <span>{to}</span>
                      <ChevronDown
                        size={10} strokeWidth={2}
                        style={{
                          color: "rgba(255,255,255,0.30)",
                          transform: toOpen ? "rotate(180deg)" : undefined,
                          transition: "transform 0.2s",
                        }}
                      />
                    </button>
                    <AnimatePresence>
                      {toOpen && (
                        <DarkCalcDropdown
                          currencies={receiveCurrencies}
                          selected={to}
                          onSelect={(c) => { setTo(c); setToOpen(false); }}
                          triggerRef={toRef}
                        />
                      )}
                    </AnimatePresence>
                  </div>
                </div>

                <button
                  data-testid="hero-quote-cta"
                  onClick={handleCTA}
                  className="btn-primary w-full justify-center rounded-xl py-2.5 text-[12px]"
                >
                  Send this transfer <ArrowRight size={11} strokeWidth={2.5} />
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
